// HomePage.js — 다크 "Engineering Studio" 메인 (브랜드 영상 풀블리드 히어로)
import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import API from '../utils/api';
import { resolveAssetUrl } from '../utils/config';
import '../styles/homepage.css';

const METRICS = [
  { n: '20', unit: '+', label: '완료 프로젝트' },
  { n: '80', unit: '%', label: '재의뢰율' },
  { n: '3', unit: '년', label: '평균 파트너십' },
  { n: '24', unit: 'h', label: '평균 응답' },
];

const SERVICES = [
  { idx: '01', title: 'UI/UX 디자인', desc: '사용자 리서치 기반의 직관적 인터페이스와 디자인 시스템을 설계합니다.', tags: ['UX 리서치', '프로토타이핑', '디자인 시스템'] },
  { idx: '02', title: '웹 개발', desc: '반응형 웹사이트부터 복잡한 웹 애플리케이션·관리자 시스템까지.', tags: ['React / Next', 'Spring Boot', 'AWS 운영'] },
  { idx: '03', title: '모바일 앱', desc: 'iOS·Android 네이티브와 크로스플랫폼으로 완성도 높은 앱을.', tags: ['React Native', 'Flutter', '앱스토어 출시'] },
];

const STACK = ['React', 'Spring Boot', 'React Native', 'Flutter', 'AWS', 'Figma'];

// How we build — 아이소메트릭 스택(위→아래)
const LAYERS = [
  { label: 'UI/UX', sub: '인터페이스 · 디자인 시스템' },
  { label: 'FRONTEND', sub: 'React · 반응형 웹/앱' },
  { label: 'BACKEND', sub: 'Spring Boot · API' },
  { label: 'INFRA', sub: 'AWS · 배포 · 운영' },
];

const getThumb = (images) => {
  const first = Array.isArray(images) ? images[0] : images;
  const path = first && typeof first === 'object' ? first.url : first;
  return path ? resolveAssetUrl(path) : null;
};

const HomePage = () => {
  const [work, setWork] = useState([]);

  useEffect(() => {
    let ignore = false;
    API.get('/portfolios', { params: { page: 1, size: 4 } })
      .then((res) => {
        const c = res.data?.content;
        const list = c?.list || (Array.isArray(c) ? c : []);
        if (!ignore) setWork(list.slice(0, 4));
      })
      .catch(() => { if (!ignore) setWork([]); });
    return () => { ignore = true; };
  }, []);

  return (
    <div className="home">
      <Seo
        description="아이디어를 제품으로. 웹·앱·UI/UX를 기획부터 개발·운영까지 함께하는 서울의 소프트웨어 스튜디오 이지랩입니다."
        path="/"
      />
      {/* HERO — 브랜드 영상 풀블리드 */}
      <section className="home-hero">
        <div className="home-hero__media" aria-hidden="true">
          <video autoPlay loop muted playsInline>
            <source src="/images/intro.mp4" type="video/mp4" />
          </video>
          <div className="home-hero__scrim" />
          <div className="home-hero__grid" />
        </div>

        <div className="home-hero__inner">
          <div className="home-eyebrow">
            <span className="home-eyebrow__bar" />
            <span className="home-mono">Software Studio — Seoul</span>
          </div>
          <h1 className="home-hero__title">아이디어를<br /><span className="home-grad">제품</span>으로 만듭니다.</h1>
          <p className="home-hero__lead">
            기획 · 디자인 · 개발 · 운영까지. 각 분야 전문가가 처음부터 끝까지 책임지는 개발 스튜디오, 이지랩.
          </p>
          <div className="home-hero__actions">
            <Link className="btn btn--accent btn--lg" to="/contact">프로젝트 시작하기 <span className="home-arw">→</span></Link>
            <Link className="btn btn--ondark btn--lg" to="/portfolio">작업 보기</Link>
          </div>
          <div className="home-stack">
            <span className="home-mono home-stack__label">Stack</span>
            <div className="home-stack__chips">
              {STACK.map((s) => <span key={s} className="home-chip">{s}</span>)}
            </div>
          </div>
        </div>
      </section>

      <div className="home-body">
        {/* METRICS */}
        <section className="home-wrap home-metrics-wrap">
          <div className="home-metrics">
            {METRICS.map((m) => (
              <div key={m.label} className="home-metric">
                <div className="home-metric__n">{m.n}<b>{m.unit}</b></div>
                <div className="home-metric__l">{m.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* SERVICES */}
        <section className="home-wrap home-section">
          <div className="home-sechead">
            <div>
              <span className="home-mono">/ Services</span>
              <h2 className="home-h2">한 팀에서, 끝까지.</h2>
            </div>
            <p className="home-sechead__desc">디자인과 개발을 분리하지 않습니다. 같은 팀이 설계부터 배포·운영까지 이어갑니다.</p>
          </div>
          <div className="home-svc">
            {SERVICES.map((s) => (
              <Link key={s.idx} to="/services" className="home-svc__item">
                <div className="home-mono home-svc__idx">{s.idx}</div>
                <h3 className="home-svc__title">{s.title}</h3>
                <p className="home-svc__desc">{s.desc}</p>
                <ul className="home-svc__list">{s.tags.map((t) => <li key={t}>{t}</li>)}</ul>
              </Link>
            ))}
          </div>
        </section>

        {/* HOW WE BUILD — 아이소메트릭 스택 도면 */}
        <section className="home-wrap home-section home-build">
          <div className="home-sechead">
            <div>
              <span className="home-mono">/ How we build</span>
              <h2 className="home-h2">한 층씩, 정확하게.</h2>
            </div>
            <p className="home-sechead__desc">인프라부터 UI까지 — 제품을 층으로 설계하고, 한 팀이 전 층을 책임집니다.</p>
          </div>
          <div className="home-build__stage">
            <svg
              className="home-build__svg"
              viewBox="0 0 460 420"
              role="img"
              aria-label="인프라·백엔드·프론트엔드·UI/UX 층으로 쌓은 제품 구조 도면"
            >
              {[3, 2, 1, 0].map((i) => {
                const cy = 60 + i * 88;
                const cx = 160, w = 100, h = 52, d = 16;
                const top = `${cx},${cy - h} ${cx + w},${cy} ${cx},${cy + h} ${cx - w},${cy}`;
                const left = `${cx - w},${cy} ${cx},${cy + h} ${cx},${cy + h + d} ${cx - w},${cy + d}`;
                const right = `${cx + w},${cy} ${cx},${cy + h} ${cx},${cy + h + d} ${cx + w},${cy + d}`;
                const layer = LAYERS[i];
                return (
                  <g key={layer.label} className="home-build__plate">
                    <polygon points={left} className="bp-face bp-face--l" />
                    <polygon points={right} className="bp-face bp-face--r" />
                    <polygon points={top} className="bp-face bp-face--top" />
                    <line x1={cx + w} y1={cy} x2={cx + w + 40} y2={cy - 14} className="bp-leader" />
                    <circle cx={cx + w} cy={cy} r="2.4" className="bp-node" />
                    <text x={cx + w + 48} y={cy - 15} className="bp-label">{layer.label}</text>
                    <text x={cx + w + 48} y={cy + 2} className="bp-sub">{layer.sub}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        </section>

        {/* WORK — 실제 포트폴리오 최근 4건 */}
        <section className="home-wrap home-section">
          <div className="home-sechead">
            <div>
              <span className="home-mono">/ Selected Work</span>
              <h2 className="home-h2">최근 작업</h2>
            </div>
            <Link to="/portfolio" className="home-morelink">전체 보기 →</Link>
          </div>
          {work.length > 0 ? (
            <div className="home-work">
              {work.map((p) => {
                const thumb = getThumb(p.images);
                return (
                  <Link key={p.id} to={`/portfolio/${p.id}`} className="home-card">
                    <div className="home-card__thumb" style={thumb ? { backgroundImage: `url("${thumb}")` } : undefined} />
                    <div className="home-card__body">
                      {p.type && <div className="home-mono home-card__type">{p.type}</div>}
                      <h3 className="home-card__title">{p.title}</h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <Link to="/portfolio" className="home-work-empty">
              포트폴리오에서 저희의 작업을 확인해 보세요 <span className="home-arw">→</span>
            </Link>
          )}
        </section>

        {/* PRODUCTS — 자체 서비스 */}
        <section className="home-wrap home-section">
          <div className="home-sechead">
            <div>
              <span className="home-mono">/ Our Products</span>
              <h2 className="home-h2">직접 만들어 운영하는 서비스</h2>
            </div>
            <p className="home-sechead__desc">고객 프로젝트만이 아니라, 저희가 직접 기획·개발·운영하는 서비스입니다.</p>
          </div>
          <div className="home-svc home-svc--products">
            <a
              href="https://lotto.ezylab.co.kr"
              target="_blank"
              rel="noopener noreferrer"
              className="home-svc__item"
            >
              <div className="home-mono home-svc__idx">P1</div>
              <h3 className="home-svc__title">포춘로또</h3>
              <p className="home-svc__desc">로또 당첨번호 조회·통계 분석과 무료 사주 풀이, AI 번호 추천까지. 웹과 iOS·Android 앱으로 서비스 중입니다.</p>
              <ul className="home-svc__list">
                <li>당첨번호·통계</li>
                <li>AI 번호 추천</li>
                <li>무료 사주·운세</li>
              </ul>
            </a>
            <a
              href="https://salarynote.ezylab.co.kr"
              target="_blank"
              rel="noopener noreferrer"
              className="home-svc__item"
            >
              <div className="home-mono home-svc__idx">P2</div>
              <h3 className="home-svc__title">월급노트</h3>
              <p className="home-svc__desc">중도퇴사 연말정산 환급, 프리랜서 3.3%, 주휴수당, 4대보험까지. 복잡한 세금·급여를 몇 번의 입력으로 계산해 주는 웹 서비스입니다.</p>
              <ul className="home-svc__list">
                <li>연말정산 환급</li>
                <li>프리랜서·일용직 세금</li>
                <li>주휴수당·4대보험</li>
              </ul>
            </a>
          </div>
        </section>

        {/* STATEMENT */}
        <section className="home-statement">
          <span className="home-statement__mark" aria-hidden="true" />
          <p className="home-statement__q">큰 회사의 복잡함 없이,<br />검증된 <span className="home-accent">전문성</span>으로.</p>
          <div className="home-mono home-statement__by">— EzyLab, 개발 파트너</div>
        </section>

        {/* CTA */}
        <section className="home-wrap">
          <div className="home-cta">
            <h2 className="home-cta__title">다음 제품, 같이 만들까요?</h2>
            <p className="home-cta__desc">아이디어만 있어도 충분합니다. 나머지는 저희가 구체화합니다.</p>
            <div className="home-cta__actions">
              <Link className="btn btn--accent btn--lg" to="/contact">프로젝트 문의 <span className="home-arw">→</span></Link>
              <Link className="btn btn--ondark btn--lg" to="/portfolio">작업 더 보기</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;
